import React, { useEffect, useState } from 'react';

import {
  blocoDe,
  faixaDoRascunho,
  IPeriodoCarteira,
  NOMES_MES,
  QUINZENA_SO_EM_DEMONSTRACOES,
  TIPOS_PERIODO,
  TipoPeriodo,
  ultimoMesDe,
} from '../regrasDaCarteira';
import { Anterior, Calendario, Check, Proximo } from './icones';

interface IProps {
  periodo: IPeriodoCarteira;
  texto: string;
  anos: number[];
  hoje: Date;
  onAplicar: (p: IPeriodoCarteira) => void;
  onAnterior: () => void;
  onProximo: () => void;
  semAnterior: boolean;
  semProximo: boolean;
}

const SETA: React.CSSProperties = {
  width: 36,
  height: 36,
  border: '1px solid #1B6FD0',
  borderRadius: 8,
  background: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export function PeriodoDaCarteira({
  periodo,
  texto,
  anos,
  hoje,
  onAplicar,
  onAnterior,
  onProximo,
  semAnterior,
  semProximo,
}: IProps) {
  const [aberto, setAberto] = useState(false);
  const [r, setR] = useState<IPeriodoCarteira>(periodo);
  // personalizado: o primeiro clique marca o início e o segundo, o fim
  const [espera, setEspera] = useState(false);

  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (ev: PointerEvent) => {
      const alvo = ev.target as HTMLElement | null;
      if (!alvo?.closest?.('.cpWrap')) setAberto(false);
    };
    const tecla = (ev: KeyboardEvent) => ev.key === 'Escape' && setAberto(false);
    document.addEventListener('pointerdown', fora, true);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('pointerdown', fora, true);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberto]);

  const abrir = () => {
    if (aberto) {
      setAberto(false);
      return;
    }
    setR(periodo);
    setEspera(false);
    setAberto(true);
  };

  const ultimo = Math.max(...r.anos.map(y => ultimoMesDe(y, hoje)));
  // em janeiro o ano corrente ainda não tem mês revisado: os atalhos usam o anterior
  const anoAtual =
    ultimoMesDe(hoje.getFullYear(), hoje) < 0 ? hoje.getFullYear() - 1 : hoje.getFullYear();
  const ultimoAtual = ultimoMesDe(anoAtual, hoje);

  const escolher = (m: number) => {
    if (r.tipo === 'personalizado') {
      if (!espera) {
        setR({ ...r, mIni: m, mFim: m });
        setEspera(true);
      } else {
        setR({ ...r, mIni: Math.min(r.mIni, m), mFim: Math.max(r.mFim, m) });
        setEspera(false);
      }
      return;
    }
    const [a, b] = blocoDe(r.tipo, m, ultimo);
    setR({ ...r, mIni: a, mFim: b });
  };

  const mudarTipo = (tipo: TipoPeriodo) => {
    if (tipo === 'personalizado') {
      setR({ ...r, tipo });
    } else {
      const [a, b] = blocoDe(tipo, r.mFim, ultimo);
      setR({ ...r, tipo, mIni: a, mFim: b });
    }
    setEspera(false);
  };

  const atalhos: [string, IPeriodoCarteira | null][] = [
    ['Esta quinzena', null],
    ['Este mês', { anos: [anoAtual], mIni: ultimoAtual, mFim: ultimoAtual, tipo: 'mensal' }],
    [
      'Últimos 3 meses',
      {
        anos: [anoAtual],
        mIni: Math.max(0, ultimoAtual - 2),
        mFim: ultimoAtual,
        tipo: 'personalizado',
      },
    ],
    [
      'Últimos 6 meses',
      {
        anos: [anoAtual],
        mIni: Math.max(0, ultimoAtual - 5),
        mFim: ultimoAtual,
        tipo: 'personalizado',
      },
    ],
    ['Este ano', { anos: [anoAtual], mIni: 0, mFim: ultimoAtual, tipo: 'anual' }],
    ['Tudo', { anos: [...anos], mIni: 0, mFim: 11, tipo: 'anual' }],
  ];

  return (
    <div className="cpWrap" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: '#64748B',
        }}
      >
        Período
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          className="cpBtn"
          onClick={abrir}
          aria-expanded={aberto}
          aria-haspopup="dialog"
          style={{
            height: 36,
            padding: '0 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            border: 0,
            borderRadius: 8,
            background: '#1B6FD0',
            color: '#FFFFFF',
            fontSize: 12.5,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <Calendario tam={14} cor="#FFFFFF" traco={2} />
          Período {texto}
        </button>
        <button
          type="button"
          aria-label="Mês anterior"
          onClick={onAnterior}
          disabled={semAnterior}
          style={{
            ...SETA,
            cursor: semAnterior ? 'default' : 'pointer',
            opacity: semAnterior ? 0.4 : 1,
          }}
        >
          <Anterior tam={14} cor="#1B6FD0" traco={2.4} />
        </button>
        <button
          type="button"
          aria-label="Próximo mês"
          onClick={onProximo}
          disabled={semProximo}
          style={{
            ...SETA,
            cursor: semProximo ? 'default' : 'pointer',
            opacity: semProximo ? 0.4 : 1,
          }}
        >
          <Proximo tam={14} cor="#1B6FD0" traco={2.4} />
        </button>
      </div>
      {aberto && (
        <div className="cpPop" role="dialog" aria-label="Escolher período da carteira">
          <div className="cpCorpo">
            <div role="radiogroup" aria-label="Periodicidade">
              <span className="cpTit">Periodicidade</span>
              {TIPOS_PERIODO.map(([k, nome]) => {
                const ativo = r.tipo === k;
                const indisp = k === 'quinzenal';
                return (
                  <button
                    type="button"
                    key={k}
                    className={`cpTipo${ativo ? ' on' : ''}`}
                    role="radio"
                    aria-checked={ativo}
                    disabled={indisp}
                    title={indisp ? QUINZENA_SO_EM_DEMONSTRACOES : undefined}
                    onClick={() => mudarTipo(k)}
                  >
                    <span className="cpRadio" />
                    {nome}
                  </button>
                );
              })}
            </div>
            <div>
              <div className="cpAnos" role="group" aria-label="Anos">
                <span className="cpTit" style={{ margin: 0 }}>
                  Anos
                </span>
                {[...anos].reverse().map(y => {
                  const ativo = r.anos.includes(y);
                  return (
                    <button
                      type="button"
                      key={y}
                      className={`cpAno${ativo ? ' on' : ''}`}
                      aria-pressed={ativo}
                      onClick={() => {
                        // sempre fica pelo menos um ano marcado
                        if (ativo && r.anos.length === 1) return;
                        setR({ ...r, anos: ativo ? r.anos.filter(x => x !== y) : [...r.anos, y] });
                      }}
                    >
                      {y}
                    </button>
                  );
                })}
              </div>
              <div className="cpGrade">
                {NOMES_MES.map((nome, i) => {
                  const indisp = i > ultimo;
                  const dentro = !indisp && i >= r.mIni && i <= r.mFim;
                  let classe = 'cpMes';
                  if (indisp) classe += ' off';
                  else if (dentro) classe += ' sel';
                  return (
                    <button
                      type="button"
                      key={nome}
                      className={classe}
                      disabled={indisp}
                      aria-pressed={dentro}
                      onClick={() => escolher(i)}
                    >
                      {nome}
                    </button>
                  );
                })}
              </div>
              <span className="cpFaixa">
                {faixaDoRascunho(r)}
                {espera ? ' · clique no fim do intervalo' : ''}
                {r.anos.includes(anoAtual) && r.mFim > ultimoAtual
                  ? ` · ${anoAtual} tem dados até ${NOMES_MES[ultimoAtual].toLowerCase()}`
                  : ''}
              </span>
            </div>
          </div>
          <div className="cpAtalhos">
            {atalhos.map(([nome, alvo]) => (
              <button
                type="button"
                key={nome}
                className="cpAtalho"
                disabled={!alvo}
                title={!alvo ? QUINZENA_SO_EM_DEMONSTRACOES : undefined}
                onClick={() => {
                  if (alvo) {
                    setR(alvo);
                    setEspera(false);
                  }
                }}
              >
                {nome}
              </button>
            ))}
          </div>
          <div className="cpRodape">
            <button
              type="button"
              className="cpAplicar"
              onClick={() => {
                const ult = Math.max(...r.anos.map(y => ultimoMesDe(y, hoje)));
                onAplicar({ ...r, mFim: Math.max(r.mIni, Math.min(r.mFim, ult)) });
                setAberto(false);
              }}
            >
              <Check tam={14} cor="#FFFFFF" traco={2.8} />
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
